import {
    clamp,
    cloneDeep,
    difference,
    indexOf,
    isArray,
    isNull,
    isNumber,
    pull,
    pullAll,
    range,
    toInteger,
} from 'lodash';
import { create } from 'zustand';
import { getCollisionIds, IShape } from '@/utils/collision-kit';
import { insertToArrByIndex, insertWithIndex } from '@/utils/math-kit';
import { traverseTree, traverseTrees } from '@/utils/tree-kit';
import { SizeUnitType } from '@/utils/unit-kit';
import {
    CanvasType,
    DEFAULT_STAGE_PADDING,
    RULER_BAR_SIZE,
    SHOW_RULER_BAR_STORAGE_KEY,
    StageType,
    StateType,
    type IExportState,
    type ILayerProps,
} from '@/version-types';
import {
    convertMaterialIdsToMap,
    CoreAPI,
    createLayerId,
    getBehavior,
    getState,
    parseLayerId,
    useState,
} from '../../core';
import { getStageOffset, scaleLayers } from '../utils';

export interface IStageBaseOperateData {
    oldIds: number[];
    oldIndex: number;
    newIds: number[];
    newIndex: number;
    oldStageSelection?: number[];
    newStageSelection?: number[];
}

/** 状态 */
interface IState {
    /** 容器宽度 */
    viewWidth: number;
    /** 容器高度 */
    viewHeight: number;
    /** 画布滚动 */
    stageScrollPosition: {
        left: number;
        top: number;
    };
    /** 渲染的缩放比例 */
    scale: number;
    /** 是否自动缩放 */
    isAutoScale: boolean;
    /** 激活的舞台 */
    activeStageIndex: number;
    /** 选中的舞台索引 */
    stageSelection: number[];
    /** 选中激活画布，ui 上的选中状态 */
    isPickStage: boolean;
    /** 舞台单位 */
    stageUnit: SizeUnitType;
    /** 舞台类型 */
    stageType: StageType;
    /** 画布类型 */
    canvasType: CanvasType;
    /** 舞台是否可见 */
    isStageVisible: boolean;
    /** 视口边距 */
    viewPortPadding: number;
    /** 舞台图层是否隐藏 */
    isHideLayers: boolean;
}

export interface IReplaceLayerResult {
    activeId?: string;
    wrapId?: string;
    newIds: string[];
    oldInfo: { index: number; layer: ILayerProps }[];
}

/** 动作 */
interface IAction {
    /** 销毁 */
    destroy: () => void;
    /** 设置舞台尺寸 */
    setViewInfo: (width: number, height: number) => void;
    /** 设置画布滚动 */
    setStageScrollPosition: (left: number, top: number) => void;
    /** 设置舞台缩放比例 */
    setScale: (scale: number) => void;
    /** 设置是否自动缩放 */
    setIsAutoScale: (isAutoScale: boolean) => void;
    /** 获取激活舞台的原始索引 */
    getActiveOriginIndex: () => number;
    /** 设置激活舞台的原始索引 */
    setActiveStageIndex: (stageIndex: number) => void;
    /** 设置是否选中激活画布 */
    setIsPickStage: (isPickStage: boolean) => void;
    /** 设置选中的舞台索引 */
    setStageSelection: (stageSelection: number[]) => void;
    /** 设置舞台单位 */
    setStageUnit: (stageUnit: SizeUnitType) => void;
    /** 设置舞台类型 */
    setStageType: (stageType: StageType, canvasType: CanvasType) => void;
    /** 设置舞台是否可见 */
    setIsStageVisible: (isStageVisible: boolean) => void;
    /** 添加/更新图层 */
    addOrUpdateLayer: (layer: ILayerProps, autoRerender?: boolean, rerenderOriginIndex?: number) => ILayerProps;
    /** 批量更新图层 */
    batchUpdateLayer: (layers: ILayerProps[], skipChild?: boolean, autoRerender?: boolean) => void;
    /** 替换图层 */
    replaceLayer: (
        oldIds: string[],
        newLayers?: ILayerProps[],
        restoreData?: { index: number; layer: ILayerProps }[],
    ) => IReplaceLayerResult | null;
    /** 添加/更新舞台 */
    addStages(
        data: ILayerProps[] | ILayerProps[][],
        args: {
            /** 舞台索引 */
            stageIndex?: number;
            /** 是否激活新舞台 */
            activeNewStage?: boolean;
            /** 是否缩放宽度 */
            scaleWidth?: boolean | number;
            /** 替换激活舞台 */
            replaceActiveStage?: boolean;
            /** 替换所有舞台 */
            replaceAllStage?: boolean;
            /** 指定插入舞台的原始索引 */
            originIndexes: number[];
        },
        autoRerender?: boolean,
    ): {
        addList: number[];
    } & IStageBaseOperateData;
    /** 删除舞台 */
    delStage(stageIndex: number): IStageBaseOperateData;
    /** 批量删除舞台 */
    batchDelStage(indices: number[]): IStageBaseOperateData | null;
    /** 交换舞台 */
    swapStages(
        stagesIndex: number[],
        destIndex: number,
        sourceIndex: number,
        activeIndex?: number,
    ): IStageBaseOperateData;
    /** 恢复舞台 */
    restoreStage(activeStageIndex: number, stageIndexList: number[]): void;
    /** 显示/隐藏素材层 */
    toggleMaterial: (isHidden: boolean, disableHideFn?: (layer: ILayerProps) => boolean, originIndex?: number) => void;
    /** 获取激活舞台的碰撞信息 */
    getShapes: (stageIndex: number, deep: boolean) => IShape[];
    /** 转换为形状 */
    convertToShapes: (layers: ILayerProps[], deep: boolean) => IShape[];
    /** 检测最外层 */
    tryPickWrap: (mouseRect: IShape, layers: ILayerProps[]) => string;
    /** 深度检测子图层 */
    tryPickChild: (mouseRect: IShape, layers: ILayerProps[]) => string;
    /** 检测不包括指定的id */
    tryPickWithoutId: (mouseRect: IShape, layers: ILayerProps[], withoutId: string) => string;
    /** 缩小画布 */
    zoomOut: () => void;
    /** 放大画布 */
    zoomIn: () => void;
    /** 设置视口边距 */
    setViewPortPadding: (viewPortPadding: number) => void;
    /** 设置舞台图层是否隐藏 */
    setIsHideLayers: (isHideLayers: boolean) => void;
}

/** 存储类型 */
interface IStore extends IState, IAction {}

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** 初始化状态 */
function getInitialState(identity: string) {
    return {
        viewWidth: 0,
        viewHeight: 0,
        ...getDestroyState(identity),
    };
}

// @ATTENTION: 编辑器 init 的时候会调用 destroy, 放在这里, 一开始初始化编辑器的时候就 set 的数据, 会被这里的默认值覆盖.
function getDestroyState(identity: string) {
    return {
        stageScrollPosition: {
            left: 0,
            top: 0,
        },
        scale: 1,
        isAutoScale: true,
        activeStageIndex: 0,
        stageSelection: [],
        isPickStage: false,
        stageUnit: SizeUnitType.px,
        stageType: StageType.SINGLE,
        canvasType: CanvasType.NORMAL,
        isStageVisible: true,
        viewPortPadding: DEFAULT_STAGE_PADDING,
        isHideLayers: false,
    };
}
/** 创建存储 */
const createState = (identity: string) => {
    return create<IStore>((set, get) => ({
        ...getInitialState(identity),

        destroy() {
            set(getDestroyState(identity));
        },

        setViewInfo(width, height) {
            set({ viewWidth: width, viewHeight: height });
        },

        setStageScrollPosition(left, top) {
            set({ stageScrollPosition: { left, top } });
        },

        setScale(scale) {
            const oldScale = get().scale;
            if (scale !== oldScale) {
                set({ scale });
            }
        },

        setIsAutoScale(isAutoScale) {
            set({ isAutoScale });
        },

        zoomOut() {
            set({ isAutoScale: false });
            const scale = get().scale;
            let newScale = Math.max(0.15, scale * 0.9);
            newScale = Math.floor(newScale * 10) / 10;
            if (newScale !== scale) {
                set({ scale: newScale });
            }
        },

        zoomIn() {
            set({ isAutoScale: false });
            const scale = get().scale;
            let newScale = Math.min(4, scale * 1.1);
            newScale = Math.ceil(newScale * 10) / 10;
            if (newScale !== scale) {
                set({ scale: newScale });
            }
        },

        getActiveOriginIndex() {
            return CoreAPI(identity).convertStageToOrigin(get().activeStageIndex);
        },

        setActiveStageIndex(stageIndex) {
            set({ activeStageIndex: stageIndex });
        },

        setIsPickStage(isPickStage) {
            set({ isPickStage });
        },

        setStageSelection(stageSelection) {
            set({ stageSelection });
        },

        setStageUnit(stageUnit) {
            set({ stageUnit });
        },

        setStageType(stageType, canvasType) {
            set({ stageType, canvasType });
        },

        setViewPortPadding(viewPortPadding) {
            set({ viewPortPadding });
        },

        setIsStageVisible(isStageVisible) {
            set({ isStageVisible });
        },

        setIsHideLayers(isHideLayers) {
            set({ isHideLayers });
        },

        addOrUpdateLayer(layer, autoRerender = true, rerenderOriginIndex?: number) {
            const res = parseLayerId(layer.id);
            let originIndex =
                rerenderOriginIndex ?? (isNull(res.originIndex) ? get().getActiveOriginIndex() : res.originIndex);

            const coreApi = CoreAPI(identity);
            const flatLayersMap = coreApi.flatLayersMap;
            const info = coreApi.tryGetOriginIdsAndIndex(originIndex, true);
            const originIds = info.originIds;
            originIndex = info.originIndex;

            const isOldOriginIndex = coreApi.checkIsOldOriginIndex(originIndex);

            function hasId(id: string) {
                return indexOf(originIds, id) !== -1;
            }
            function pushRenderId(id: string) {
                if (!hasId(id)) {
                    originIds.push(id);
                    coreApi.setRerender(originIndex);
                }
            }

            function updateLayer(layer: ILayerProps, isRoot: boolean) {
                getBehavior(identity, layer)?.onBeforeAddOrUpdate?.(layer);
                createLayerId(layer, originIndex);
                (layer.children || []).forEach((item) => {
                    updateLayer(item, false);
                });
                if (isRoot && !layer.parentId) {
                    if (coreApi.checkIsExtraGroup(layer)) {
                        (layer.children || []).forEach((item) => {
                            pushRenderId(item.id);
                        });
                    }
                    pushRenderId(layer.id);
                }
                convertMaterialIdsToMap(layer);
                flatLayersMap.set(layer.id, layer, true, isRoot);
            }
            updateLayer(layer, true);
            const renderStageList = coreApi.getRenderStage();
            if (renderStageList.indexOf(originIndex) === -1 && !isOldOriginIndex) {
                renderStageList.push(originIndex);
            }
            if (autoRerender) {
                if (isNumber(rerenderOriginIndex)) {
                    coreApi.setRerender(rerenderOriginIndex);
                }
                coreApi.rerender();
            }
            return layer;
        },

        batchUpdateLayer(layers, skipChild = false, autoRerender = true) {
            const coreApi = CoreAPI(identity);
            const updateLayer = (layer: ILayerProps) => {
                const newLayer = { ...layer };
                if (!skipChild) {
                    (newLayer.children || []).forEach((item) => {
                        updateLayer(item);
                    });
                }
                coreApi.flatLayersMap.set(layer.id, newLayer, true, !newLayer.parentId);
            };
            for (let i = 0; i < layers.length; i++) {
                const layer = layers[i];
                updateLayer(layer);
            }
            if (autoRerender && layers.length > 0) {
                coreApi.rerender();
            }
        },

        replaceLayer(oldIds, newLayers, restoreData) {
            const { originIndex } = parseLayerId(oldIds[0]);
            if (!isNumber(originIndex) || (!newLayers && !restoreData)) {
                return null;
            }
            const coreApi = CoreAPI(identity);
            // 替换操作，判断数据是否合法
            // oldIds 图层必须属于同一级
            const groupId = coreApi.getLayer(oldIds[0])?.parentId;
            for (let i = 0; i < oldIds.length; i++) {
                const id = oldIds[i];
                const layer: ILayerProps = coreApi.getLayer(id);
                if (!layer) return null;
                // 如果 rootId 存在，则 layer.parentId 必须存在且等于 rootId
                if (groupId) {
                    if (layer.parentId !== groupId) return null;
                } else {
                    if (layer.parentId) return null;
                }
            }

            const originSortIds = groupId
                ? (coreApi.getLayer(groupId).children || []).map((v) => v.id)
                : coreApi.getRenderIds(originIndex);
            const delInfo: { index: number; layer: ILayerProps }[] = [];
            const collectIndex = (id: string, isRoot = false) => {
                const layer: ILayerProps = coreApi.getLayer(id, true);
                if (layer) {
                    (layer.children || []).forEach((item) => {
                        collectIndex(item.id);
                    });
                    if (isRoot) {
                        const index = originSortIds.indexOf(layer.id);
                        delInfo.push({
                            index,
                            layer,
                        });
                    }
                    coreApi.flatLayersMap.delete(layer.id, false);
                }
            };

            if (newLayers) {
                traverseTrees(newLayers, (layer) => {
                    createLayerId(layer, originIndex);
                });
            }
            // 收集待删除的索引信息
            for (let i = 0; i < oldIds.length; i++) {
                collectIndex(oldIds[i], true);
            }
            if (!delInfo.length) {
                return null;
            }
            const sortedDelInfo = delInfo.sort((a, b) => {
                return a.index - b.index;
            });
            // 执行替换操作
            if (newLayers) {
                if (!groupId) {
                    // 删除一级图层，需要剔除渲染id
                    pullAll(
                        originSortIds,
                        sortedDelInfo.map((item) => item.layer.id),
                    );
                    const minIndex = delInfo[0].index;
                    const originIds = coreApi.getRenderIds(originIndex);
                    const newIds = insertToArrByIndex(
                        cloneDeep(originIds),
                        minIndex,
                        newLayers.map((item) => item.id),
                    );
                    originIds.length = 0;
                    originIds.push(...newIds);
                    for (let i = 0; i < newLayers.length; i++) {
                        this.addOrUpdateLayer(newLayers[i], false, originIndex);
                    }
                } else {
                    const oldLayer = coreApi.getLayer(groupId, true);
                    const newChild = cloneDeep(
                        (oldLayer.children = oldLayer.children.filter((v) => oldIds.indexOf(v.id) === -1)),
                    );
                    const minIndex = delInfo[0].index;
                    for (let i = 0; i < newLayers.length; i++) {
                        const layer = newLayers[i];
                        layer.parentId = groupId;
                    }
                    oldLayer.children.length = 0;
                    oldLayer.children.push(...insertToArrByIndex(newChild, minIndex, newLayers));
                    this.addOrUpdateLayer(oldLayer, false, originIndex);
                }
                coreApi.setRerender(originIndex);
                coreApi.rerender();
                return {
                    activeId: oldIds.length === 1 ? oldIds[0] : undefined,
                    wrapId: groupId ? coreApi.getWrapLayer(groupId, false)?.id : undefined,
                    newIds: newLayers.map((v) => v.id),
                    oldInfo: delInfo,
                };
            } else if (restoreData) {
                // 历史记录恢复
                if (!groupId) {
                    pullAll(
                        originSortIds,
                        sortedDelInfo.map((item) => item.layer.id),
                    );
                    const insetMap = new Map<number, string>();
                    for (let i = 0; i < restoreData.length; i++) {
                        const item = restoreData[i];
                        insetMap.set(item.index, item.layer.id);
                    }
                    insertWithIndex(originSortIds, insetMap);
                    for (let i = 0; i < restoreData.length; i++) {
                        const item = restoreData[i];
                        traverseTree(item.layer, (layer) => {
                            coreApi.flatLayersMap.set(layer.id, layer, false, false);
                        });
                        coreApi.flatLayersMap.set(item.layer.id, item.layer, false, !item.layer.parentId);
                    }
                } else {
                    const oldLayer = coreApi.getLayer(groupId, true);
                    const newChild = cloneDeep(
                        (oldLayer.children = oldLayer.children.filter((v) => oldIds.indexOf(v.id) === -1)),
                    );
                    const insetMap = new Map<number, string>();
                    const newLayers: ILayerProps[] = [];
                    for (let i = 0; i < restoreData.length; i++) {
                        const item = restoreData[i];
                        insetMap.set(item.index, item.layer.id);
                        newLayers.push(item.layer);
                        coreApi.flatLayersMap.set(item.layer.id, item.layer, false, !item.layer.parentId);
                    }
                    const minIndex = delInfo[0].index;
                    oldLayer.children.length = 0;
                    oldLayer.children.push(...insertToArrByIndex(newChild, minIndex, newLayers));
                    this.addOrUpdateLayer(oldLayer, false, originIndex);
                }
                coreApi.setRerender(originIndex);
                coreApi.rerender();
            }

            return null;
        },

        addStages(
            data,
            { stageIndex, activeNewStage, scaleWidth, replaceActiveStage, replaceAllStage, originIndexes },
        ) {
            // console.log('ot addStages-->', {
            //     data,
            //     stageIndex,
            //     activeNewStage,
            //     scaleWidth,
            //     replaceActiveStage,
            //     replaceAllStage,
            //     originIndexes,
            // });
            const coreApi = CoreAPI(identity);
            let originIndex = 0;
            const addOriginIndexList: number[] = [];

            const oldStageList = cloneDeep(coreApi.getRenderStage());
            const oldActiveStageIndex = get().activeStageIndex;

            let newStageList = oldStageList;
            let newActiveStageIndex = oldActiveStageIndex;

            const setRenderSortIdsArr = (i: number, index?: number) => {
                if (i !== 0) return;
                originIndex = isNumber(index) ? index : coreApi.getOriginIdsList().length;
                addOriginIndexList.push(originIndex);
                if (isNumber(stageIndex)) {
                    stageIndex = clamp(stageIndex, 0, coreApi.getRenderStageLength() - 1);
                    const stageList = coreApi.getRenderStage();
                    // stageList.splice(stageIndex, 0, originIndex);
                    insertToArrByIndex(stageList, stageIndex, originIndex, false);
                } else {
                    coreApi.getRenderStage().push(originIndex);
                }
                newStageList = cloneDeep(coreApi.getRenderStage());
                newActiveStageIndex = newStageList.indexOf(originIndex);
            };

            const addOrUpdateLayer = get().addOrUpdateLayer;
            data.forEach((item: ILayerProps | ILayerProps[], i) => {
                if (isArray(item)) {
                    item.forEach((layer, j) => {
                        setRenderSortIdsArr(j, originIndexes[i]);
                        addOrUpdateLayer(layer, false, originIndex);
                    });
                } else {
                    setRenderSortIdsArr(i, originIndexes[0]);
                    addOrUpdateLayer(item, false, originIndex);
                }
            });

            if (activeNewStage) {
                set({ activeStageIndex: newActiveStageIndex });
            }

            // 换算宽度
            if (scaleWidth) {
                const newOriginIndex = coreApi.convertStageToOrigin(newActiveStageIndex);
                const bgLayer = coreApi.getBackgroundLayer(newOriginIndex);
                if (bgLayer) {
                    const { width, height } = bgLayer;
                    const stageIndex = get().activeStageIndex;
                    const size = coreApi.getSizeByIndex(stageIndex);
                    const currentWidth = isNumber(scaleWidth) ? scaleWidth : size.width;
                    if (width !== currentWidth) {
                        const oldSize = { width, height };
                        const newSize = {
                            width: currentWidth,
                            height: toInteger(currentWidth / (width / height)),
                        };
                        const layers = cloneDeep(coreApi.getLayers(newOriginIndex));
                        scaleLayers(identity, layers, oldSize, newSize, false, true, false, (layer) => {
                            coreApi.flatLayersMap.set(layer.id, layer, false, !layer.parentId);
                        });
                    }
                }
            }

            // 替换当前模版
            if (replaceActiveStage) {
                newActiveStageIndex = oldActiveStageIndex;
                newStageList = cloneDeep(oldStageList);
                newStageList[oldActiveStageIndex] = originIndex;
                coreApi.setRenderStage(newStageList);
            }

            // 替换全部模版
            if (replaceAllStage) {
                newActiveStageIndex = 0;
                newStageList = difference(newStageList, oldStageList);
                coreApi.setRenderStage(newStageList);
                set({ activeStageIndex: newActiveStageIndex });
            }

            coreApi.setRerenderSort();
            coreApi.rerender();

            return {
                addList: addOriginIndexList,
                oldIds: oldStageList,
                oldIndex: oldActiveStageIndex,
                newIds: newStageList,
                newIndex: newActiveStageIndex,
            };
        },

        delStage(stageIndex) {
            const coreApi = CoreAPI(identity);
            const len = coreApi.getRenderStageLength();
            const oldIndex = clamp(stageIndex, 0, len - 1);
            const oldIds = cloneDeep(coreApi.getRenderStage());
            const num = oldIds[oldIndex];
            pull(coreApi.getRenderStage(), num);

            const newIds = cloneDeep(coreApi.getRenderStage());
            // 删除后移动到下一张
            let newIndex;
            if (oldIndex >= oldIds.length - 1) {
                // 如果删除的是最后一张,选中新的最后一张
                newIndex = newIds.length - 1;
            } else {
                // 否则选中下一张
                newIndex = oldIndex;
            }

            set({ activeStageIndex: newIndex });
            coreApi.setRerenderSort();
            coreApi.rerender();
            return {
                oldIds,
                oldIndex,
                newIds,
                newIndex,
            };
        },

        batchDelStage(indices) {
            const coreApi = CoreAPI(identity);
            const len = coreApi.getRenderStageLength();

            // 如果要删除的数量等于总数量，至少保留一个舞台
            if (len === indices.length) {
                indices.pop(); // 移除最后一个要删除的索引
            }

            if (len === 1 || indices.length === 0) {
                return null;
            }

            // 按照索引从大到小排序，避免删除时索引变化
            const sortedIndices = [...indices].sort((a, b) => b - a);

            // 记录删除前的状态
            const oldIds = cloneDeep(coreApi.getRenderStage());
            const oldIndex = get().activeStageIndex;

            const renderStageList = coreApi.getRenderStage();
            // 执行删除操作
            sortedIndices.forEach((index) => {
                const num = renderStageList[index];
                pull(renderStageList, num);
            });

            // 更新后的状态
            const newIds = cloneDeep(renderStageList);

            // 计算新的激活索引
            let newIndex;
            const maxDeletedIndex = Math.max(...indices);

            if (maxDeletedIndex >= oldIds.length - 1) {
                // 如果删除的包含最后一张,选中新的最后一张
                newIndex = newIds.length - 1;
            } else {
                // 否则选中删除区间后的第一张
                const minDeletedIndex = Math.min(...indices);
                newIndex = minDeletedIndex;
            }
            set({ activeStageIndex: newIndex });
            coreApi.setRerenderSort();
            coreApi.rerender();

            return {
                oldIds,
                oldIndex,
                newIds,
                newIndex,
            };
        },

        swapStages(stagesIndex, destIndex, sourceIndex, activeIndex = stagesIndex[0]) {
            const coreApi = CoreAPI(identity);
            const oldIds = cloneDeep(coreApi.getRenderStage());
            const oldOriginIndexes = stagesIndex.map((index) => oldIds[index]);
            pull(coreApi.getRenderStage(), ...oldOriginIndexes);
            let sortUp = sourceIndex > destIndex; // 是否向上移动
            let insertIndex = coreApi.getRenderStage().indexOf(oldIds[destIndex]);
            // 如果目标索引在已选范围内
            if (stagesIndex.includes(destIndex)) {
                const newIds = cloneDeep(coreApi.getRenderStage());
                // 获取已选位置的完整连续范围
                const selectionRange = range(stagesIndex[0], stagesIndex[stagesIndex.length - 1] + 1);
                // 获取已选位置的完整连续范围中未被选中的元素数组
                const excludeRange = difference(selectionRange, stagesIndex);
                if (destIndex > excludeRange[excludeRange.length - 1]) {
                    const destIndexOld = excludeRange[excludeRange.length - 1];
                    const destOriginIndexOld = oldIds.indexOf(destIndexOld);
                    insertIndex = newIds.indexOf(destOriginIndexOld);
                    sortUp = false;
                } else if (destIndex < excludeRange[0]) {
                    const prevDestIndexOld = stagesIndex[0] - 1;
                    if (prevDestIndexOld < 0) {
                        insertIndex = 0;
                    } else {
                        const prevDestOriginIndexOld = newIds[prevDestIndexOld];
                        insertIndex = newIds.indexOf(prevDestOriginIndexOld) + 1;
                    }
                    sortUp = true;
                }
            }
            const list = insertToArrByIndex(cloneDeep(coreApi.getRenderStage()), insertIndex, oldOriginIndexes, sortUp);
            coreApi.setRenderStage(list);
            const newIds = cloneDeep(coreApi.getRenderStage());
            const newIndex = newIds.indexOf(oldIds[activeIndex]);
            set({ activeStageIndex: newIndex });
            coreApi.setRerenderSort();
            coreApi.rerender();
            return {
                oldIds,
                oldIndex: activeIndex,
                newIds,
                newIndex,
                oldStageSelection: stagesIndex,
                newStageSelection: oldOriginIndexes.map((index) => newIds.indexOf(index)),
            };
        },

        restoreStage(activeStageIndex, stageIndexList) {
            set({ activeStageIndex });
            const coreApi = CoreAPI(identity);
            coreApi.setRenderStage(stageIndexList);
            coreApi.setRerenderSort();
            coreApi.rerender();
        },

        toggleMaterial(isHidden: boolean, disableHideFn?: (layer: ILayerProps) => boolean, originIndex?: number) {
            const coreApi = CoreAPI(identity);
            const index = isNumber(originIndex) ? originIndex : get().getActiveOriginIndex();
            const layers = coreApi.getLayers(index);
            let count = 0;
            layers.forEach((item) => {
                const disableCollision = disableHideFn
                    ? disableHideFn(item)
                    : getBehavior(identity, item.layerType)?.checkDisableCollision?.(item);

                if (!disableCollision && item.__isHidden !== isHidden) {
                    const layer = cloneDeep(item);
                    layer.__isHidden = isHidden;
                    coreApi.flatLayersMap.set(layer.id, layer, true, false);
                    count++;
                }
            });
            if (count) {
                coreApi.rerender();
            }
            console.log('ot toggleMaterial', { isHidden, originIndex, count });
        },

        getShapes(stageIndex: number, deep = false) {
            const coreApi = CoreAPI(identity);
            const originIndex = coreApi.convertStageToOrigin(stageIndex);
            const collisionLayers = coreApi.getCollisionLayers(originIndex);
            return get().convertToShapes(collisionLayers, deep);
        },

        convertToShapes(layers: ILayerProps[], deep: boolean) {
            const resShapes = Array<IShape>();
            if (!layers?.length) return resShapes;
            const stageScale = get().scale;
            const { originIndex } = parseLayerId(layers[0].id);
            const coreApi = CoreAPI(identity);
            const offsetStage = getStageOffset(identity, coreApi.convertOriginToStage(originIndex || 0), true);
            const getLayersShape = (layers: ILayerProps[], deep: boolean) => {
                function convertToShape(layer: ILayerProps) {
                    const { id, left, top, width, height, rotate } = layer;
                    const res: IShape = {
                        id,
                        left: left * stageScale + offsetStage.left,
                        top: top * stageScale + offsetStage.top,
                        width: (width || 1) * stageScale,
                        height: (height || 1) * stageScale,
                        rotate,
                    };
                    return res;
                }
                for (let i = layers.length - 1; i >= 0; i--) {
                    const layer = layers[i];
                    if (deep) {
                        // 普通点击选中时，需要排除点击到组
                        if (!coreApi.checkIsGroup(layer)) {
                            resShapes.push(convertToShape(layer));
                        }
                        getLayersShape(layer.children || [], deep);
                    } else {
                        // command成组时，只检测第一层
                        resShapes.push(convertToShape(layer));
                    }
                }
            };

            getLayersShape(layers, deep);

            return resShapes;
        },

        tryPickWrap(mouseRect: IShape, layers: ILayerProps[]) {
            const shapes = get().convertToShapes(layers, false);
            const ids = getCollisionIds(mouseRect, shapes);
            return ids[0] ?? '';
        },

        tryPickChild(mouseRect: IShape, layers: ILayerProps[]) {
            const shapes = get().convertToShapes(layers, true);
            const ids = getCollisionIds(mouseRect, shapes);
            return ids[0] ?? '';
        },

        tryPickWithoutId(mouseRect: IShape, layers: ILayerProps[], withoutId: string) {
            const shapes = get().convertToShapes(layers, false);
            const ids = getCollisionIds(mouseRect, shapes);
            pull(ids, withoutId);
            return ids[0] ?? '';
        },
    }));
};

/** 存储名称 */
const name = 'framework-stage-state';

/** hook 获取存储 */
export const useStageState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getStageState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

const exportState: IExportState = {
    name,
    createState,
};

/** 导出存储 */
export default exportState;
